"use client";

import React, { useEffect, useRef, useState } from "react";
import { Sparkles, PenTool, Megaphone, Briefcase, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const services = [{
  title: "Clinical Excellence",
  subheader: "EXPERT CARE",
  description: "Advanced aesthetic treatments performed with medical precision and professional expertise in Norwich.",
  icon: Sparkles
}, {
  title: "Bespoke Artistry",
  subheader: "PERSONALIZED",
  description: "Tailored beauty solutions designed to enhance your natural features and your unique radiance.",
  icon: PenTool
}, {
  title: "Ethical Luxury",
  subheader: "CONSCIOUS",
  description: "Premium products and sustainable practices that prioritize your wellbeing and the planet.",
  icon: Megaphone
}, {
  title: "Holistic Calm",
  subheader: "WELLBEING",
  description: "A sanctuary of tranquility where every treatment is a journey towards complete relaxation and peace.",
  icon: Briefcase
}];
export default function OurServices() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="184756" id="whychoosenatalia" className="bg-background py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_center,var(--color-primary)_0%,transparent_70%)] opacity-[0.1] pointer-events-none" />

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        
        {}
        <div ref={headerRef} className={`flex flex-col items-center text-center mb-20 transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="font-sans tracking-[0.4em] uppercase text-xs font-semibold text-muted-foreground mb-4 block">
            NATALIA'S BEAUTY
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground font-light tracking-tight mb-6">
            Why Choose Our Clinic
          </h2>
          <Separator className="w-16 bg-ring h-[1px]" />
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {services.map((service, i) => {
          const Icon = service.icon;
          return <div key={i} data-index={i} className={`col-span-1 md:col-span-6 lg:col-span-3 transition-all duration-1000 transform ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <Card className="h-full bg-card text-card-foreground border-border/40 rounded-xl transition-all duration-700 hover:-translate-y-3 hover:border-ring/30 hover:shadow-xl group overflow-hidden relative">
                  
                  {}
                  <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

                  <CardHeader className="pt-10 pb-4 px-8 relative z-10">
                    <div className="mb-8 inline-flex items-center justify-center p-3 rounded-lg bg-primary/20 text-ring group-hover:scale-110 transition-transform duration-700">
                      <Icon size={24} strokeWidth={1} />
                    </div>
                    <div className="font-sans tracking-[0.25em] uppercase text-[9px] text-muted-foreground mb-2">
                      {service.subheader}
                    </div>
                    <CardTitle className="font-serif text-2xl font-normal tracking-wide text-foreground">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="px-8 pb-10 relative z-10">
                    <CardDescription className="font-sans text-sm text-muted-foreground leading-relaxed font-light">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        {}
        <div ref={ctaRef} className={`mt-24 flex flex-col sm:flex-row items-center justify-center gap-6 transition-all duration-1000 transform ${isCtaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Link to="https://nataliasbeauty.com/the-clinic/">
            <Button variant="outline" size="lg" className="rounded-lg border-ring text-foreground hover:bg-ring hover:text-primary-foreground font-sans tracking-widest uppercase text-[10px] px-10 h-14 transition-all duration-500">
              Discover The Clinic
            </Button>
          </Link>
          <Link to="tel:07766733976">
            <Button variant="default" size="lg" className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/80 font-sans tracking-widest uppercase text-[10px] px-10 h-14 group transition-all duration-500">
              Book Consultation
              <ArrowRight className="ml-2 size-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
            </Button>
          </Link>
        </div>

      </div>
    </section>;
}